import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWeightHanging, faBoxes, faBox, faTree, faThLarge, 
  faPallet, faTruckLoading, faWineGlass, faClock, faArrowsAltH, 
  faCircleNotch, faChevronLeft, faCheckCircle, faUndo
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';
import { useCalculatorData } from './useCalculatorData';

const goldColor = '#c5a059';

const getIconByKey = (key) => {
  const icons = {
    ton: faWeightHanging,
    big_box: faBox,
    small_box: faBoxes,
    tiles: faThLarge,
    wood: faTree,
    pallet: faPallet,
    tech: faTruckLoading,
    fragile: faWineGlass
  };
  return icons[key] || faBox;
};

const FuraCalculator = ({ assets, onResultChange }) => {
  const { t } = useTranslation();
  const { meta, loading: metaLoading } = useCalculatorData(7);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  
  const [data, setData] = useState({
    assetId: null,
    cat: '',
    qty: '',
    weight: '',
    hours: '',
    dist: ''
  });

  const dbCategories = useMemo(() => {
    if (!meta?.assets) return [];
    return meta.assets
      .filter(asset => {
        const id = Number(asset.id);
        const gId = Number(asset.group_id);
        return gId === 700 || (id >= 7000 && id <= 7999);
      })
      .map(asset => {
        let opts = {};
        try {
          opts = typeof asset.options === 'string' ? JSON.parse(asset.options) : (asset.options || {});
        } catch (e) { opts = { key: 'ton' }; }
        
        return {
          id: asset.id,
          label: asset.name,
          key: opts.key || 'ton',
          icon: getIconByKey(opts.key),
          color: asset.color || goldColor
        };
      });
  }, [meta.assets]);

  const currentCat = useMemo(() => 
    dbCategories.find(c => c.id === data.assetId), 
  [dbCategories, data.assetId]);

  const calculate = async () => {
    if (data.cat === 'tech' && (!data.hours || data.hours <= 0)) {
        alert(t('fill_hours_alert'));
        return;
    }
    if (data.cat !== 'tech' && (!data.qty || data.qty <= 0)) {
        alert(t('fill_qty_alert'));
        return;
    }

    setLoading(true);
    try {
        const response = await axios.post(`${API_BASE}/api/calculators/calculate`, {
            calculatorId: 7,
            category: 'LOGISTICS',
            data: {
                assetId: data.assetId,
                qty: parseFloat(data.qty) || 0,
                weight: parseFloat(data.weight) || 0,
                hours: parseFloat(data.hours) || 0,
                dist: parseFloat(data.dist) || 0,
                cat: data.cat
            }
        });

        if (response.data) {
            const { totalPrice, summary, business } = response.data;
            const finalPrice = totalPrice || 0;
            const assetName = currentCat ? currentCat.label : t('logistics');

            const combinedMessage = `
🚚 ${t('service')}: ${assetName}
📦 ${t('cargo_type')}: ${data.cat}
🔢 ${t('qty_label')}: ${data.qty || 0} ${data.cat === 'wood' ? t('unit_m3') : t('unit_pcs')}
⚖️ ${t('weight_label')}: ${data.weight || 0} ${t('unit_ton')}
📏 ${t('distance')}: ${data.dist || 0} ${t('unit_meter')}
-------------------------
💰 ${t('total_price')}: ${finalPrice.toLocaleString()} ֏`.trim();

            setServerResult({ total: finalPrice, summary });

            if (onResultChange) {
                onResultChange(finalPrice, summary, {
                    totalPrice: finalPrice,
                    total_price: finalPrice,
                    details: summary,
                    message: combinedMessage,
                    category_name: 'LOGISTICS',
                    business: business,
                    qty: data.qty,
                    weight: data.weight
                });
            }
            setStep(3);
        }
    } catch (error) {
        alert(t('error'));
    } finally {
        setLoading(false);
    }
  };

  const handleEdit = () => {
    setStep(2);
    if (onResultChange) onResultChange(0, "", null);
  };

  if (metaLoading) {
    return (
      <div style={{...s.container, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'300px'}}>
        <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color={goldColor} />
      </div>
    );
  }

  return (
    <div style={s.container}>
      {step === 1 && (
        <div style={s.fade}>
          <p style={s.subtitle}>{t('select_cargo_type')}</p>
          <div style={s.menuGrid}>
            {dbCategories.map(item => (
              <button key={item.id} 
                style={{
                    ...s.menuBtn, 
                    borderColor: data.assetId === item.id ? item.color : '#f1f5f9'
                }}
                onClick={() => { 
                  setData({...data, assetId: item.id, cat: item.key}); 
                  setStep(2); 
                }}>
                <div style={{...s.iconCircle, background: item.color + '15'}}>
                    <FontAwesomeIcon icon={item.icon} size="lg" color={item.color} />
                </div>
                <b style={s.btnLabel}>{item.label}</b>
              </button>
            ))}
          </div>
          {dbCategories.length === 0 && (
            <p style={{textAlign: 'center', color: '#94a3b8', marginTop: '20px'}}>{t('no_data_found')} (ID: 7)</p>
          )}
        </div>
      )}

      {step === 2 && currentCat && (
        <div style={s.fade}>
          <div style={{...s.catBadge, background: currentCat.color + '15', color: currentCat.color}}>
              <FontAwesomeIcon icon={currentCat.icon} style={{marginRight: '8px'}} />
              {currentCat.label.toUpperCase()}
          </div>

          <div style={s.inputGrid}>
            {data.cat !== 'tech' && (
              <div style={s.inputGroup}>
                <label style={s.smallL}>
                  <FontAwesomeIcon icon={faBoxes} style={s.labelIcon} /> 
                  {t('qty_label')} ({data.cat === 'wood' ? t('unit_m3') : t('unit_pcs')})
                </label>
                <input type="number" value={data.qty} onChange={e => setData({...data, qty: e.target.value})} style={s.in} placeholder="0" />
              </div>
            )}

            {data.cat === 'tech' && (
              <div style={s.inputGroup}>
                <label style={s.smallL}><FontAwesomeIcon icon={faClock} style={s.labelIcon} /> {t('hours_label')}</label>
                <input type="number" value={data.hours} onChange={e => setData({...data, hours: e.target.value})} style={s.in} placeholder={t('unit_hour')} />
              </div>
            )}

            {(data.cat !== 'wood' && data.cat !== 'tech' && data.cat !== 'fragile') && (
              <div style={s.inputGroup}>
                <label style={s.smallL}><FontAwesomeIcon icon={faWeightHanging} style={s.labelIcon} /> {t('weight_label')}</label>
                <input type="number" value={data.weight} onChange={e => setData({...data, weight: e.target.value})} style={s.in} placeholder={t('unit_ton')} />
              </div>
            )}

            <div style={s.inputGroup}>
              <label style={s.smallL}><FontAwesomeIcon icon={faArrowsAltH} style={s.labelIcon} /> {t('distance_label')}</label>
              <input type="number" value={data.dist} onChange={e => setData({...data, dist: e.target.value})} style={s.in} placeholder={t('dist_placeholder')} />
            </div>
          </div>

          <button style={s.mainBtn} onClick={calculate} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
          </button>
          
          <button style={s.backLink} onClick={() => { setStep(1); if(onResultChange) onResultChange(0,"",null); }}>
            <FontAwesomeIcon icon={faChevronLeft} size="xs" /> {t('back_to_menu')}
          </button>
        </div>
      )}

      {step === 3 && serverResult && (
        <div style={s.fade}>
          <div style={s.successHeader}>
            <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" />
            <h3 style={s.successTitle}>{t('calculation_ready')}</h3>
          </div>

          <div style={s.finalCard}>
            <div style={s.priceBox}>
              <span style={s.priceTitle}>{t('total_sum')}</span>
              <div style={s.priceText}>
                {(serverResult.total || 0).toLocaleString()} ֏
              </div>
            </div>
            
            <div style={s.details}>
              <div style={s.row}><span>{t('cargo_type')}:</span> <b>{currentCat?.label}</b></div>
              {data.qty && <div style={s.row}><span>{t('qty_label')}:</span> <b>{data.qty} {data.cat === 'wood' ? t('unit_m3') : t('unit_pcs')}</b></div>}
              {data.weight && <div style={s.row}><span>{t('weight_label')}:</span> <b>{data.weight} {t('unit_ton')}</b></div>}
              {data.hours && <div style={s.row}><span>{t('time')}:</span> <b>{data.hours} {t('unit_hour')}</b></div>}
              <div style={s.row}><span>{t('distance')}:</span> <b>{data.dist || 0} {t('unit_meter')}</b></div>
            </div>
          </div>

          <button style={s.secondaryBtn} onClick={handleEdit}>
            <FontAwesomeIcon icon={faUndo} style={{marginRight: '8px'}} /> {t('modify')}
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { width: '100%', maxWidth: '600px', margin: '0 auto', padding: '10px' },
  subtitle: { textAlign: 'center', color: '#64748b', fontSize: '13px', marginBottom: '20px', fontWeight: '600' },
  fade: { animation: 'fadeIn 0.3s ease-in' },
  menuGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  menuBtn: { padding: '20px 10px', borderRadius: '20px', border: '2px solid #f1f5f9', background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'all 0.2s' },
  iconCircle: { width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },
  btnLabel: { fontSize: '11px', color: '#334155', fontWeight: '700' },
  catBadge: { padding: '12px', borderRadius: '14px', textAlign: 'center', marginBottom: '20px', fontSize: '12px', fontWeight: '800' },
  inputGrid: { display: 'flex', flexDirection: 'column', gap: '15px' },
  inputGroup: { display: 'flex', flexDirection: 'column' },
  in: { padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0', fontSize: '16px', fontWeight: '700', background: '#f8fafc', color: '#1e293b', outline: 'none' },
  smallL: { fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '700' },
  labelIcon: { marginRight: '6px', color: goldColor },
  mainBtn: { width: '100%', padding: '18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '16px', marginTop: '20px', fontWeight: '800', cursor: 'pointer' },
  secondaryBtn: { width: '100%', padding: '18px', background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '16px', marginTop: '12px', fontWeight: '800', cursor: 'pointer' },
  backLink: { width: '100%', background: 'none', border: 'none', color: '#94a3b8', marginTop: '15px', cursor: 'pointer', fontWeight: '600' },
  successHeader: { textAlign: 'center', marginBottom: '20px' },
  successTitle: { fontSize: '18px', fontWeight: '800', marginTop: '10px' },
  finalCard: { background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '15px' },
  priceBox: { background: '#0f172a', color: '#fff', padding: '25px', textAlign: 'center' },
  priceTitle: { fontSize: '10px', opacity: 0.6, letterSpacing: '1px' },
  priceText: { fontSize: '32px', fontWeight: '900', color: '#10b981' },
  details: { padding: '20px' },
  row: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }
};

export default FuraCalculator;